import { motion } from "framer-motion";
import { Chapter, MaskedLines, Fade, EASE } from "./motion";

const TERMS = ["EDUCATION", "CAPABILITY", "COMMUNICATION", "INDUSTRY", "CAREER"];

const BrokenLink = ({ delay }) => (
    <div className="flex min-w-[28px] flex-1 items-center gap-1.5 md:min-w-[64px] md:gap-2.5">
        <motion.span
            className="h-px flex-1 origin-left bg-slate/40"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE, delay }}
        />
        <svg className="h-3 w-3 shrink-0 text-cobalt" viewBox="0 0 12 12" aria-hidden>
            <path d="M2 2 L10 10 M10 2 L2 10" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        <motion.span
            className="h-px flex-1 origin-right bg-slate/40"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE, delay: delay + 0.1 }}
        />
    </div>
);

export default function Problem() {
    return (
        <section className="bg-white py-20 md:py-40" data-testid="problem-section">
            <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16">
                <Chapter index="CH.01" title="The Problem" />

                <div className="mt-16 grid gap-14 md:mt-24 lg:grid-cols-12 lg:gap-10">
                    <div className="lg:col-span-8">
                        <MaskedLines
                            stagger={0.14}
                            lineClassName="font-display text-[clamp(2.2rem,8vw,5.8rem)] font-semibold leading-[1.12] tracking-[-0.02em] text-ink"
                            lines={[
                                <span key="a">The degree certifies</span>,
                                <span key="b">
                                    knowledge. <span className="text-slate">Not</span>
                                </span>,
                                <span key="c" className="text-cobalt">capability.</span>,
                            ]}
                        />
                    </div>
                    <div className="flex flex-col justify-end lg:col-span-4">
                        <Fade delay={0.4}>
                            <p className="text-base leading-relaxed text-slate md:text-lg">
                                Engineering education delivers theory at scale. Industry hires
                                for capability, communication, and proof of work. Between the two
                                sits a distance most students are asked to cross alone.
                            </p>
                            <p className="mt-6 text-[11px] uppercase tracking-[0.3em] text-cobalt">
                                QRUS exists inside that distance.
                            </p>
                        </Fade>
                    </div>
                </div>

                <Fade delay={0.2} className="mt-20 md:mt-28">
                    <div className="border border-ink/10 bg-paper px-6 py-10 md:px-12 md:py-14">
                        <div className="mb-10 flex items-center justify-between">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-slate">
                                FIG.02 — The Disconnect Map
                            </span>
                            <span className="hidden text-[10px] uppercase tracking-[0.3em] text-slate md:block">
                                Connections: Broken
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-y-6">
                            {TERMS.map((term, i) => (
                                <div key={term} className="flex min-w-0 flex-1 basis-full items-center sm:basis-auto">
                                    <Fade delay={0.15 + i * 0.12} y={12} className="shrink-0">
                                        <span
                                            className={`border px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] md:px-5 md:py-3 md:text-xs ${
                                                i === 0 || i === TERMS.length - 1
                                                    ? "border-ink/25 bg-white text-ink"
                                                    : "border-ink/15 bg-white text-slate"
                                            }`}
                                        >
                                            {term}
                                        </span>
                                    </Fade>
                                    {i < TERMS.length - 1 && <BrokenLink delay={0.3 + i * 0.12} />}
                                </div>
                            ))}
                        </div>
                        <p className="mt-10 max-w-lg text-sm leading-relaxed text-slate">
                            Five words that should form one continuous system — today, they
                            don&rsquo;t. Each break is where a career stalls.
                        </p>
                    </div>
                </Fade>
            </div>
        </section>
    );
}
